import React from 'react';
import { useSupabaseClient } from '../utils/supabase';
import { ENDPOINTS } from '../config';
import { AlertTriangle, Trash2, Edit, Plus, X, Bell, MessageSquare, Layout, Copy, Check, Terminal } from 'lucide-react';

// Type definitions
type DisplayType = 'toast' | 'card' | 'modal';
type Position = 'bottom-right' | 'top-right' | 'center';
type Behavior = 'timeout' | 'first-to-accept' | 'persistent';

interface AlertType {
  id: string;
  name: string;
  description: string;
  display_type: DisplayType;
  position: Position;
  behavior: Behavior;
  timeout_duration?: number;
  design_config: {
    backgroundColor: string;
    textColor: string;
    accentColor: string;
    icon: string;
    borderRadius: string;
  };
  endpoint_url: string;
  user_id: string;
  call_count: number;
}

function AlertTypes() {
  const [alertTypes, setAlertTypes] = React.useState<AlertType[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [showAddModal, setShowAddModal] = React.useState(false);
  const [showEditModal, setShowEditModal] = React.useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);
  const [selectedAlertType, setSelectedAlertType] = React.useState<AlertType | null>(null);
  const [copiedUrlId, setCopiedUrlId] = React.useState<string | null>(null);
  const [copiedCurlId, setCopiedCurlId] = React.useState<string | null>(null);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const [successMessage, setSuccessMessage] = React.useState<string | null>(null);
  const [isProcessing, setIsProcessing] = React.useState(false);
  const [newAlertType, setNewAlertType] = React.useState({
    name: '',
    description: '',
    display_type: 'toast' as DisplayType,
    position: 'bottom-right' as Position,
    behavior: 'persistent' as Behavior,
    timeout_duration: 5,
    design_config: {
      backgroundColor: '#ffffff',
      textColor: '#000000',
      accentColor: '#4f46e5',
      icon: 'bell',
      borderRadius: '0.5rem'
    }
  });

  const supabase = useSupabaseClient();
  const baseWebhookUrl = ENDPOINTS.webhook;

  React.useEffect(() => {
    fetchAlertTypes();
  }, []);

  const showMessage = (message: string, isError = false) => {
    if (isError) {
      setErrorMessage(message);
      setSuccessMessage(null);
    } else {
      setSuccessMessage(message);
      setErrorMessage(null);
    }

    // Auto-clear messages after 3 seconds
    setTimeout(() => {
      setErrorMessage(null);
      setSuccessMessage(null);
    }, 3000);
  };

  const fetchAlertTypes = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('alert_types')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching alert types:', error);
        showMessage(`Error fetching alert types: ${error.message}`, true);
        return;
      }

      if (data) {
        setAlertTypes(data);
      }
    } catch (err) {
      console.error('Unexpected error fetching alert types:', err);
      showMessage('An unexpected error occurred. Please try again.', true);
    }
    finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsProcessing(true);
    setErrorMessage(null);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        showMessage('User not authenticated', true);
        setIsProcessing(false);
        return;
      }

      const { data, error } = await supabase
        .from('alert_types')
        .insert([{
          ...newAlertType,
          user_id: user.id
        }])
        .select();

      if (error) {
        console.error('Error creating alert type:', error);
        showMessage(`Error creating alert type: ${error.message}`, true);
        setIsProcessing(false);
        return;
      }

      showMessage('Alert type created successfully!');
      setShowAddModal(false);
      setNewAlertType({
        name: '',
        description: '',
        display_type: 'toast',
        position: 'bottom-right',
        behavior: 'persistent',
        timeout_duration: 5,
        design_config: {
          backgroundColor: '#ffffff',
          textColor: '#000000',
          accentColor: '#4f46e5',
          icon: 'bell',
          borderRadius: '0.5rem'
        }
      });
      fetchAlertTypes();
    } catch (err) {
      console.error('Unexpected error creating alert type:', err);
      showMessage('An unexpected error occurred. Please try again.', true);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleEdit = (alertType: AlertType) => {
    setSelectedAlertType(alertType);
    setShowEditModal(true);
    setErrorMessage(null);
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedAlertType) return;
    
    setIsProcessing(true);
    setErrorMessage(null);
    
    try {
      const { error } = await supabase
        .from('alert_types')
        .update({
          name: selectedAlertType.name,
          description: selectedAlertType.description,
          display_type: selectedAlertType.display_type,
          position: selectedAlertType.position,
          behavior: selectedAlertType.behavior,
          timeout_duration: selectedAlertType.timeout_duration,
          design_config: selectedAlertType.design_config
        })
        .eq('id', selectedAlertType.id);

      if (error) {
        console.error('Error updating alert type:', error);
        showMessage(`Error updating alert type: ${error.message}`, true);
        return setIsProcessing(false);
      }
      
      showMessage('Alert type updated successfully!');
      setShowEditModal(false);
      setSelectedAlertType(null);
      fetchAlertTypes();
    } catch (err) {
      console.error('Unexpected error updating alert type:', err);
      showMessage('An unexpected error occurred while updating.', true);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeleteClick = (alertType: AlertType) => {
    setSelectedAlertType(alertType);
    setShowDeleteConfirm(true);
    setErrorMessage(null);
  };

  const handleDelete = async () => {
    if (!selectedAlertType) return;
    
    setIsProcessing(true);
    setErrorMessage(null);
    
    try {
      const { data, error } = await supabase
        .from('alert_types')
        .delete() 
        .eq('id', selectedAlertType.id);

      if (error) {
        console.error('Error deleting alert type:', error);
        showMessage(`Error deleting alert type: ${error.message}`, true);
        return setIsProcessing(false);
      }
      
      showMessage('Alert type deleted successfully!');
      setShowDeleteConfirm(false);
      setSelectedAlertType(null);
      fetchAlertTypes();
    } catch (err) {
      console.error('Unexpected error deleting alert type:', err);
      showMessage('An unexpected error occurred while deleting.', true);
    } finally {
      setIsProcessing(false);
    }
  };

  const getDisplayIcon = (type: DisplayType) => {
    switch (type) {
      case 'toast':
        return <Bell className="h-5 w-5" />;
      case 'card':
        return <MessageSquare className="h-5 w-5" />;
      case 'modal':
        return <Layout className="h-5 w-5" />;
      default:
        return <Bell className="h-5 w-5" />;
    }
  };

  const getWebhookUrl = (endpointUrl: string) => {
    return `${baseWebhookUrl}/${endpointUrl}`;
  };

  const handleCopyUrl = async (id: string, url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedUrlId(id);
      setTimeout(() => setCopiedUrlId(null), 2000);
    } catch (err) {
      console.error('Failed to copy URL:', err);
    }
  };

  const getCurlCommand = (alertType: AlertType) => {
    const url = getWebhookUrl(alertType.endpoint_url);
    return `curl -X POST ${url} \\
  -H "Content-Type: application/json" \\
  -H "Accept: application/json" \\
  -H "X-API-Key: ${alertType.api_key}" \\
  -d '{
    "title": "Your notification title",
    "message": "Your notification message",
    "type": "info"
  }'`;
  };

  const handleCopyCurl = async (id: string, alertType: AlertType) => {
    try {
      await navigator.clipboard.writeText(getCurlCommand(alertType));
      setCopiedCurlId(id);
      setTimeout(() => setCopiedCurlId(null), 2000);
    } catch (err) {
      console.error('Failed to copy cURL command:', err);
    }
  };

  return (
    <div>
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900">Alert Types</h1>
          <p className="mt-2 text-sm text-gray-700">
            Configure different types of alerts and their behaviors.
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          <button
            type="button"
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:w-auto"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Alert Type
          </button>
        </div>
      </div>
      
      {/* Notification messages */}
      {errorMessage && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <AlertTriangle className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{errorMessage}</p>
            </div>
          </div>
        </div>
      )}
      
      {successMessage && (
        <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <Check className="h-5 w-5 text-green-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-green-700">{successMessage}</p>
            </div>
          </div>
        </div>
      )}
      
      <div className="mt-8 flex flex-col">
        <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                      Name
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Type
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Behavior
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Endpoint URL
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Call Count
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      API Key
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      cURL Command
                    </th>
                    <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {loading ? (
                    <tr>
                      <td colSpan={6} className="py-6 text-center text-sm text-gray-500">
                        <div className="flex justify-center items-center space-x-2">
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-500" />
                          <span>Loading alert types...</span>
                        </div>
                      </td>
                    </tr>
                  ) : alertTypes.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="py-6 text-center text-sm text-gray-500">
                        No alert types found. Add one to get started.
                      </td>
                    </tr>
                  ) : (
                    alertTypes.map((alertType) => (
                      <tr key={alertType.id}>
                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                          <div className="flex items-center">
                            {getDisplayIcon(alertType.display_type)}
                            <span className="ml-2">{alertType.name}</span>
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {alertType.display_type} ({alertType.position})
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {alertType.behavior}
                          {alertType.behavior === 'timeout' && ` (${alertType.timeout_duration}s)`}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 font-mono">
                          <div className="flex items-center space-x-2">
                            <div className="max-w-md truncate" title={getWebhookUrl(alertType.endpoint_url)}>
                              {getWebhookUrl(alertType.endpoint_url)}
                            </div>
                            <button
                              onClick={() => handleCopyUrl(alertType.id, getWebhookUrl(alertType.endpoint_url))}
                              className="text-gray-400 hover:text-gray-600"
                              title="Copy URL"
                              aria-label="Copy webhook URL"
                            >
                              {copiedUrlId === alertType.id ? (
                                <Check className="h-4 w-4 text-green-500" />
                              ) : (
                                <Copy className="h-4 w-4" />
                              )}
                            </button>
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {alertType.call_count || 0}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 font-mono">
                          <div className="flex items-center space-x-2">
                            <div className="max-w-md truncate" title={alertType.api_key}>
                              {alertType.api_key.substring(0, 8)}...
                            </div>
                            <button
                              onClick={() => handleCopyUrl(alertType.id + '-key', alertType.api_key)}
                              className="text-gray-400 hover:text-gray-600"
                              title="Copy API Key"
                              aria-label="Copy API Key"
                            >
                              {copiedUrlId === alertType.id + '-key' ? (
                                <Check className="h-4 w-4 text-green-500" />
                              ) : (
                                <Copy className="h-4 w-4" />
                              )}
                            </button>
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          <button
                            onClick={() => handleCopyCurl(alertType.id, alertType)}
                            className="inline-flex items-center px-3 py-1 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                            title="Copy cURL command"
                            aria-label="Copy cURL command"
                          >
                            {copiedCurlId === alertType.id ? (
                              <>
                                <Check className="h-4 w-4 text-green-500 mr-2" />
                                Copied!
                              </>
                            ) : (
                              <>
                                <Terminal className="h-4 w-4 mr-2" />
                                Copy cURL
                              </>
                            )}
                          </button>
                        </td>
                        <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                          <button 
                            onClick={() => handleEdit(alertType)}
                            className="text-indigo-600 hover:text-indigo-900 mr-4"
                            title="Edit alert type"
                          >
                            <Edit className="h-5 w-5" />
                          </button>
                          <button 
                            onClick={() => handleDeleteClick(alertType)}
                            className="text-red-600 hover:text-red-900"
                            title="Delete alert type"
                          >
                            <Trash2 className="h-5 w-5" />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Add Alert Type Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity z-50">
          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <div className="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div className="absolute top-0 right-0 hidden pt-4 pr-4 sm:block">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                  >
                    <span className="sr-only">Close</span>
                    <X className="h-6 w-6" />
                  </button>
                </div>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Basic Information */}
                  <div>
                    <h3 className="text-lg font-medium leading-6 text-gray-900">Basic Information</h3>
                    <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                      <div className="sm:col-span-6">
                        <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                          Name
                        </label>
                        <input
                          type="text"
                          name="name"
                          id="name"
                          required
                          value={newAlertType.name}
                          onChange={(e) => setNewAlertType({ ...newAlertType, name: e.target.value })}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        />
                      </div>

                      <div className="sm:col-span-6">
                        <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                          Description
                        </label>
                        <textarea
                          id="description"
                          name="description"
                          rows={3}
                          value={newAlertType.description}
                          onChange={(e) => setNewAlertType({ ...newAlertType, description: e.target.value })}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Display Settings */}
                  <div>
                    <h3 className="text-lg font-medium leading-6 text-gray-900">Display Settings</h3>
                    <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                      <div className="sm:col-span-3">
                        <label htmlFor="display_type" className="block text-sm font-medium text-gray-700">
                          Display Type
                        </label>
                        <select
                          id="display_type"
                          name="display_type"
                          value={newAlertType.display_type}
                          onChange={(e) => setNewAlertType({ ...newAlertType, display_type: e.target.value as DisplayType })}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        >
                          <option value="toast">Toast</option>
                          <option value="card">Card</option>
                          <option value="modal">Modal</option>
                        </select>
                      </div>

                      <div className="sm:col-span-3">
                        <label htmlFor="position" className="block text-sm font-medium text-gray-700">
                          Position
                        </label>
                        <select
                          id="position"
                          name="position"
                          value={newAlertType.position}
                          onChange={(e) => setNewAlertType({ ...newAlertType, position: e.target.value as Position })}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        >
                          <option value="bottom-right">Bottom Right</option>
                          <option value="top-right">Top Right</option>
                          <option value="center">Center</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Behavior Settings */}
                  <div>
                    <h3 className="text-lg font-medium leading-6 text-gray-900">Behavior Settings</h3>
                    <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                      <div className="sm:col-span-3">
                        <label htmlFor="behavior" className="block text-sm font-medium text-gray-700">
                          Behavior
                        </label>
                        <select
                          id="behavior"
                          name="behavior"
                          value={newAlertType.behavior}
                          onChange={(e) => setNewAlertType({ ...newAlertType, behavior: e.target.value as Behavior })}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        >
                          <option value="timeout">Timeout</option>
                          <option value="first-to-accept">First to Accept</option>
                          <option value="persistent">Persistent</option>
                        </select>
                      </div>

                      {newAlertType.behavior === 'timeout' && (
                        <div className="sm:col-span-3">
                          <label htmlFor="timeout_duration" className="block text-sm font-medium text-gray-700">
                            Timeout Duration (seconds)
                          </label>
                          <input
                            type="number"
                            id="timeout_duration"
                            name="timeout_duration"
                            min="1"
                            value={newAlertType.timeout_duration}
                            onChange={(e) => setNewAlertType({ ...newAlertType, timeout_duration: parseInt(e.target.value) })}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Design Settings */}
                  <div>
                    <h3 className="text-lg font-medium leading-6 text-gray-900">Design Settings</h3>
                    <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                      <div className="sm:col-span-2">
                        <label htmlFor="backgroundColor" className="block text-sm font-medium text-gray-700">
                          Background Color
                        </label>
                        <input
                          type="color"
                          id="backgroundColor"
                          name="backgroundColor"
                          value={newAlertType.design_config.backgroundColor}
                          onChange={(e) => setNewAlertType({
                            ...newAlertType,
                            design_config: { ...newAlertType.design_config, backgroundColor: e.target.value }
                          })}
                          className="mt-1 block w-full h-8 rounded-md border-gray-300"
                        />
                      </div>

                      <div className="sm:col-span-2">
                        <label htmlFor="textColor" className="block text-sm font-medium text-gray-700">
                          Text Color
                        </label>
                        <input
                          type="color"
                          id="textColor"
                          name="textColor"
                          value={newAlertType.design_config.textColor}
                          onChange={(e) => setNewAlertType({
                            ...newAlertType,
                            design_config: { ...newAlertType.design_config, textColor: e.target.value }
                          })}
                          className="mt-1 block w-full h-8 rounded-md border-gray-300"
                        />
                      </div>

                      <div className="sm:col-span-2">
                        <label htmlFor="accentColor" className="block text-sm font-medium text-gray-700">
                          Accent Color
                        </label>
                        <input
                          type="color"
                          id="accentColor"
                          name="accentColor"
                          value={newAlertType.design_config.accentColor}
                          onChange={(e) => setNewAlertType({
                            ...newAlertType,
                            design_config: { ...newAlertType.design_config, accentColor: e.target.value }
                          })}
                          className="mt-1 block w-full h-8 rounded-md border-gray-300"
                        />
                      </div>

                      <div className="sm:col-span-3">
                        <label htmlFor="icon" className="block text-sm font-medium text-gray-700">
                          Icon
                        </label>
                        <select
                          id="icon"
                          name="icon"
                          value={newAlertType.design_config.icon}
                          onChange={(e) => setNewAlertType({
                            ...newAlertType,
                            design_config: { ...newAlertType.design_config, icon: e.target.value }
                          })}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        >
                          <option value="bell">Bell</option>
                          <option value="alert-triangle">Alert Triangle</option>
                          <option value="info">Info</option>
                          <option value="check-circle">Check Circle</option>
                        </select>
                      </div>

                      <div className="sm:col-span-3">
                        <label htmlFor="borderRadius" className="block text-sm font-medium text-gray-700">
                          Border Radius
                        </label>
                        <select
                          id="borderRadius"
                          name="borderRadius"
                          value={newAlertType.design_config.borderRadius}
                          onChange={(e) => setNewAlertType({
                            ...newAlertType,
                            design_config: { ...newAlertType.design_config, borderRadius: e.target.value }
                          })}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        >
                          <option value="0">None</option>
                          <option value="0.25rem">Small</option>
                          <option value="0.5rem">Medium</option>
                          <option value="1rem">Large</option>
                          <option value="9999px">Full</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                    <button
                      type="submit"
                      disabled={isProcessing}
                      className="inline-flex w-full justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:col-start-2 sm:text-sm disabled:bg-indigo-300"
                    >
                      {isProcessing ? 'Creating...' : 'Create'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowAddModal(false)}
                      className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:col-start-1 sm:mt-0 sm:text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Alert Type Modal */}
      {showEditModal && selectedAlertType && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity z-50">
          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <div className="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div className="absolute top-0 right-0 hidden pt-4 pr-4 sm:block">
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                  >
                    <span className="sr-only">Close</span>
                    <X className="h-6 w-6" />
                  </button>
                </div>

                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                    <h3 className="text-lg font-medium leading-6 text-gray-900">Edit Alert Type</h3>
                    <div className="mt-2">
                      <div className="space-y-4">
                        <div>
                          <label htmlFor="edit-name" className="block text-sm font-medium text-gray-700">
                            Name
                          </label>
                          <input
                            type="text"
                            name="edit-name"
                            id="edit-name"
                            required
                            value={selectedAlertType.name}
                            onChange={(e) => setSelectedAlertType({ ...selectedAlertType, name: e.target.value })}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                          />
                        </div>

                        <div>
                          <label htmlFor="edit-description" className="block text-sm font-medium text-gray-700">
                            Description
                          </label>
                          <textarea
                            id="edit-description"
                            name="edit-description"
                            rows={3}
                            value={selectedAlertType.description}
                            onChange={(e) => setSelectedAlertType({ ...selectedAlertType, description: e.target.value })}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Display Settings */}
                <div>
                  <h3 className="text-lg font-medium leading-6 text-gray-900">Display Settings</h3>
                  <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                    <div className="sm:col-span-3">
                      <label htmlFor="edit-display-type" className="block text-sm font-medium text-gray-700">
                        Display Type
                      </label>
                      <select
                        id="edit-display-type"
                        name="edit-display-type"
                        value={selectedAlertType.display_type}
                        onChange={(e) => setSelectedAlertType({ ...selectedAlertType, display_type: e.target.value as DisplayType })}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      >
                        <option value="toast">Toast</option>
                        <option value="card">Card</option>
                        <option value="modal">Modal</option>
                      </select>
                    </div>

                    <div className="sm:col-span-3">
                      <label htmlFor="edit-position" className="block text-sm font-medium text-gray-700">
                        Position
                      </label>
                      <select
                        id="edit-position"
                        name="edit-position"
                        value={selectedAlertType.position}
                        onChange={(e) => setSelectedAlertType({ ...selectedAlertType, position: e.target.value as Position })}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      >
                        <option value="bottom-right">Bottom Right</option>
                        <option value="top-right">Top Right</option>
                        <option value="center">Center</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Behavior Settings */}
                <div>
                  <h3 className="text-lg font-medium leading-6 text-gray-900">Behavior Settings</h3>
                  <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                    <div className="sm:col-span-3">
                      <label htmlFor="edit-behavior" className="block text-sm font-medium text-gray-700">
                        Behavior
                      </label>
                      <select
                        id="edit-behavior"
                        name="edit-behavior"
                        value={selectedAlertType.behavior}
                        onChange={(e) => setSelectedAlertType({ ...selectedAlertType, behavior: e.target.value as Behavior })}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      >
                        <option value="timeout">Timeout</option>
                        <option value="first-to-accept">First to Accept</option>
                        <option value="persistent">Persistent</option>
                      </select>
                    </div>

                    {selectedAlertType.behavior === 'timeout' && (
                      <div className="sm:col-span-3">
                        <label htmlFor="edit-timeout" className="block text-sm font-medium text-gray-700">
                          Timeout Duration (seconds)
                        </label>
                        <input
                          type="number"
                          id="edit-timeout"
                          name="edit-timeout"
                          min="1"
                          value={selectedAlertType.timeout_duration || 5}
                          onChange={(e) => setSelectedAlertType({ ...selectedAlertType, timeout_duration: parseInt(e.target.value) })}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Design Settings */}
                <div>
                  <h3 className="text-lg font-medium leading-6 text-gray-900">Design Settings</h3>
                  <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                    <div className="sm:col-span-2">
                      <label htmlFor="edit-bg-color" className="block text-sm font-medium text-gray-700">
                        Background Color
                      </label>
                      <input
                        type="color"
                        id="edit-bg-color"
                        name="edit-bg-color"
                        value={selectedAlertType.design_config.backgroundColor}
                        onChange={(e) => setSelectedAlertType({
                          ...selectedAlertType,
                          design_config: { ...selectedAlertType.design_config, backgroundColor: e.target.value }
                        })}
                        className="mt-1 block w-full h-8 rounded-md border-gray-300"
                      />
                    </div>

                    <div className="sm:col-span-2">
                      <label htmlFor="edit-text-color" className="block text-sm font-medium text-gray-700">
                        Text Color
                      </label>
                      <input
                        type="color"
                        id="edit-text-color"
                        name="edit-text-color"
                        value={selectedAlertType.design_config.textColor}
                        onChange={(e) => setSelectedAlertType({
                          ...selectedAlertType,
                          design_config: { ...selectedAlertType.design_config, textColor: e.target.value }
                        })}
                        className="mt-1 block w-full h-8 rounded-md border-gray-300"
                      />
                    </div>

                    <div className="sm:col-span-2">
                      <label htmlFor="edit-accent-color" className="block text-sm font-medium text-gray-700">
                        Accent Color
                      </label>
                      <input
                        type="color"
                        id="edit-accent-color"
                        name="edit-accent-color"
                        value={selectedAlertType.design_config.accentColor}
                        onChange={(e) => setSelectedAlertType({
                          ...selectedAlertType,
                          design_config: { ...selectedAlertType.design_config, accentColor: e.target.value }
                        })}
                        className="mt-1 block w-full h-8 rounded-md border-gray-300"
                      />
                    </div>

                    <div className="sm:col-span-3">
                      <label htmlFor="edit-icon" className="block text-sm font-medium text-gray-700">
                        Icon
                      </label>
                      <select
                        id="edit-icon"
                        name="edit-icon"
                        value={selectedAlertType.design_config.icon}
                        onChange={(e) => setSelectedAlertType({
                          ...selectedAlertType,
                          design_config: { ...selectedAlertType.design_config, icon: e.target.value }
                        })}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      >
                        <option value="bell">Bell</option>
                        <option value="alert-triangle">Alert Triangle</option>
                        <option value="info">Info</option>
                        <option value="check-circle">Check Circle</option>
                      </select>
                    </div>

                    <div className="sm:col-span-3">
                      <label htmlFor="edit-border-radius" className="block text-sm font-medium text-gray-700">
                        Border Radius
                      </label>
                      <select
                        id="edit-border-radius"
                        name="edit-border-radius"
                        value={selectedAlertType.design_config.borderRadius}
                        onChange={(e) => setSelectedAlertType({
                          ...selectedAlertType,
                          design_config: { ...selectedAlertType.design_config, borderRadius: e.target.value }
                        })}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      >
                        <option value="0">None</option>
                        <option value="0.25rem">Small</option>
                        <option value="0.5rem">Medium</option>
                        <option value="1rem">Large</option>
                        <option value="9999px">Full</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                  <button
                    type="submit"
                    onClick={handleUpdate}
                    disabled={isProcessing}
                    className="inline-flex w-full justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:col-start-2 sm:text-sm disabled:bg-indigo-300"
                  >
                    {isProcessing ? 'Updating...' : 'Update'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:col-start-1 sm:mt-0 sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && selectedAlertType && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity z-50">
          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <div className="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <AlertTriangle className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg font-medium leading-6 text-gray-900">Delete Alert Type</h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Are you sure you want to delete the alert type "{selectedAlertType.name}"? This action cannot be undone.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    onClick={handleDelete}
                    disabled={isProcessing}
                    className="inline-flex w-full justify-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm disabled:bg-red-300"
                  >
                    {isProcessing ? 'Deleting...' : 'Delete'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowDeleteConfirm(false)}
                    className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AlertTypes